import { Box, Fade } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  IconCheck,
  IconExclamationMark,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import { useEffect } from "react";
import Text from "../ui/Text";

const ToastContainer = styled(Box)(({ variant }) => {
  const variants = {
    success: {
      background: "#F0FDF4",
      border: "1px solid #00C950",
      // color: "#00C950",
    },
    error: {
      background: "#FFF2F0", // Pink background for errors to match images
      border: "1px solid #FFCCC7",
      // color: "#FFFFFF", // White text on pink background
    },
    warning: {
      background: "#FFFBEB",
      border: "1px solid #F59E0B",
      // color: "#F59E0B",
    },
    info: {
      background: "#EFF6FF",
      border: "1px solid #3B82F6",
      // color: "#3B82F6",
    },
  };

  return {
    position: "fixed",
    bottom: "20px", // Position at bottom to match images
    right: "50%",
    transform: "translateX(50%)",
    zIndex: 1300,
    padding: "12px 16px",
    borderRadius: "2px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "85%",
    maxWidth: "400px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    ...variants[variant],
    "@media (min-width: 900px)": {
      transform: "translateX(0)",
      right: 20,
    },
  };
});

const IconContainer = styled(Box)(({ variant }) => {
  const variants = {
    success: "#00C950",
    error: "#D32F2F", // White icon on pink background
    warning: "#F59E0B",
    info: "#3B82F6",
  };

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: variants[variant],
    color: "white",
    flexShrink: 0,
    fontSize: "14px",
    fontWeight: "bold",
  };
});

const CloseButton = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  cursor: "pointer",
  color: "#666",
  marginLeft: "auto",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
});

const Toast = ({
  open,
  message,
  variant = "success",
  duration = 4000,
  onClose,
  actions,
  canRetry = false,
}) => {
  useEffect(() => {
    if (open && duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [open, duration, onClose]);

  const getIcon = () => {
    switch (variant) {
      case "success":
        return <IconCheck size={16} />;
      case "error":
        return <IconX size={16} />;
      case "warning":
        return <IconExclamationMark size={16} />;
      case "info":
        return <IconInfoCircle size={16} />;
      default:
        return <IconInfoCircle size={16} />;
    }
  };

  if (!open) return null;

  return (
    <Fade in={open}>
      <ToastContainer variant={variant}>
        <IconContainer variant={variant}>{getIcon()}</IconContainer>
        <Box sx={{ flex: 1 }}>
          {message && <Text variant="paragraph3">{message}</Text>}
        </Box>
        {(actions || canRetry) && (
          <Box sx={{ display: "flex", gap: 1, ml: 1 }}>
            {actions}
            {canRetry && (
              <Text
                variant="paragraph3"
                sx={{
                  color: variant === "error" ? "#FFFFFF" : "#1890FF",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "12px",
                }}
                onClick={() => {
                  // Handle retry logic
                  onClose?.();
                }}
              >
                Retry
              </Text>
            )}
          </Box>
        )}
        {onClose && (
          <CloseButton onClick={onClose}>
            <IconX size={14} />
          </CloseButton>
        )}
      </ToastContainer>
    </Fade>
  );
};

export default Toast;
